import gql from "graphql-tag.macro";
import { v4 as uuidv4 } from "uuid";

const queryChainInstance = gql`
    query {
        chaininstance {
            _id
            chain {
                id
                name
                description
                feature
                textPrompt
                action
                autochatPrompt
                category
                # industries
                # levels
                # positions
                criteria {
                  user{
                    role
                    level
                  }
                  company {
                    industry
                  }
                  tags
                }
                startingQuestions
                chatIntro
                temperature
                headingWorkflowFilter
                promptNodes,
                model
            }
        }
    }
`;

const gqlUpsertChainInstance = gql`
    mutation upsertChain($chain: [chainInput!]!) {
        upsertChainInstance(chain: $chain) {
            _id
            chain {
                id
                name
                description
                feature
                textPrompt
                action
                autochatPrompt
                category
                # industries
                # levels
                # positions
                criteria {
                  user{
                    role
                    level
                  }
                  company {
                    industry
                  }
                  tags
                }
                startingQuestions
                chatIntro
                temperature
                headingWorkflowFilter
                promptNodes
                model
            }
        }
    }
`;

const removeTypenameKeys = (obj, keys = ["__typename"]) =>
  obj !== Object(obj)
    ? obj
    : Array.isArray(obj)
      ? obj.map(item => removeTypenameKeys(item, keys))
      : Object.keys(obj)
        .filter(k => !keys.includes(k))
        .reduce((acc, x) => Object.assign(acc, { [x]: removeTypenameKeys(obj[x], keys) }), {});


export const loadChainInstance = async ({ client }) => {
  const {
    data: { chaininstance },
  } = await client.query({
    query: queryChainInstance,
    variables: {},
    fetchPolicy: "no-cache",
  });

  

  const cleanChainInstance = removeTypenameKeys(chaininstance);
  cleanChainInstance?.chain?.forEach(chain => {
    for (const key in chain) {
      if (chain[key] === null) {
        delete chain[key];
      }
    }
  });
  return cleanChainInstance;
};

export const upsertChainInstance = async ({ chain }, { client }) => {
  const {
    data: { upsertChainInstance: chaininstance },
  } = await client.mutate({
    mutation: gqlUpsertChainInstance,
    variables: { chain },
  });

  const cleanChainInstance = removeTypenameKeys(chaininstance);
  cleanChainInstance?.chain?.forEach(chain => {
    for (const key in chain) {
      if (chain[key] === null) {
        delete chain[key];
      }
    }
  });
  return cleanChainInstance;
};

export const chainInstanceActionResult = async ({ actionResult }) => {
  if (window?.app?.Duchess) {
    const channel = `/duchess/AIController/${actionResult.projectId}`;
    try {
      // eslint-disable-next-line new-cap
      window.app.Duchess.PublishUpdate(channel, { actionResult });
    } catch (e) {
      console.error("chaininstance.api.chainInstanceActionResult", e);
    }
  }
};

export const defaultChainInstance = () => {
  const defaultCriteria = {
    user: {
      role: [],
      level: [],
    },
    company: {
      industry: []
    },
    tags: []
  };
  return {
    chain: () => [
      {
        action: "replace",
        autochatPrompt: null,
        category: "Write content",
        // industries: null,
        // levels: null,
        criteria: defaultCriteria,
        chatIntro: null,
        description: null,
        feature: "content-generator",
        id: uuidv4(),
        name: "Write: from instructions",
        promptNodes: [
          "For context only, here is some information about my document:",
          "The title of the document is: ```{doc_title}```",
          "The brief for the document is: ```{doc_brief}```",
          "The table of contents for the document is: ```{doc_toc}```",
          "For more context, here are the details for the current heading:",
          "The brief for any parent headings of the current heading is:```{notes_parents}```",
          "The current heading is titled: ```{heading_text}```",
          "Now write content to specifically meet the following brief for the current heading: ```{notes_current}```"
        ],
        startingQuestions: null,
        temperature: 0.1,
        textPrompt: null,
      },
      {
        action: "chat",
        autochatPrompt: null,
        category: "General chat",
        criteria: defaultCriteria,
        // industries: null,
        // levels: null,
        chatIntro: null,
        description: null,
        feature: "writing-assistant",
        id: uuidv4(),
        name: "Ask: about this heading",
        promptNodes: [
          "For context only, here is some information about my document:",
          "The title of the document is: ```{doc_title}```",
          "The brief for the document is: ```{doc_brief}```",
          "The table of contents for the document is: ```{doc_toc}```",
          "For more context, here are the details for the current heading:",
          "The current heading is titled: ```{heading_text}```",
          "The brief for any parent headings of the current heading is:```{notes_parents}```",
          "The brief for the current heading is: ```{notes_current}```",
          "Do not refer to the context or brief in your response.",
          "The content I am asking about is: ```{chunk_text}```"
        ],
        startingQuestions: null,
        temperature: 1,
        textPrompt: "How can I help you?",
      },
    ]
  };
};
